// =============================================================================
// HelpSection.js — Phase 82
//
// "Replay welcome tour" is wired to reset profiles.welcome_tour_completed_at
// exactly the same way the old Settings page did — this is the ONLY
// backend-touching action here. Other tiles are simple links (mailto,
// external status page, help docs) that don't require auth.
// =============================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { SectionHeader, SettingsCard, PrimaryButton } from './primitives';
import { DemoDataSettingsCard } from './DemoDataSettingsCard';
// Phase 84.5 — Version string is now sourced from the single source of
// truth in lib/version.js. Previously this file hard-coded 'v2.10.35'
// which drifted from the shipping version every deploy.
import { APP_VERSION } from '@/lib/version';

export default function HelpSection() {
  const { user, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [replaying, setReplaying] = useState(false);

  const handleReplayTour = async () => {
    if (!user?.id) return;
    setReplaying(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ welcome_tour_completed_at: null })
        .eq('id', user.id);
      if (error) {
        toast.error(error.message || 'Could not reset the tour right now.');
        return;
      }
      try {
        const localKey = `twoone20:welcome_tour_completed:${user.id}`;
        window.localStorage.removeItem(localKey);
      } catch { /* ignore private-mode failures */ }
      await refreshProfile?.();
      toast.success('Restarting the welcome tour…');
      // v3.7.4 Phase 84.28.12 — pass ?replayTour=1 so WelcomeTour force-
      // opens regardless of whether the profile refresh has propagated
      // through the AuthContext yet. Users reported clicking Replay
      // showed the green toast but the tour never actually appeared.
      nav('/dashboard?replayTour=1');
    } finally {
      setReplaying(false);
    }
  };

  const quickActions = [
    {
      id: 'replay-tour',
      label: 'Replay welcome tour',
      desc: 'A short 7-slide refresher on what 2120 is, how we treat your data, and how to get the most out of it.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" />
          <polygon points="8,7 14,10 8,13" fill="currentColor" stroke="none" />
        </svg>
      ),
      cta: replaying ? 'Restarting…' : 'Start tour',
      action: handleReplayTour,
      disabled: replaying,
      testId: 'settings-replay-tour',
    },
    {
      id: 'help-docs',
      label: 'Help documentation',
      desc: 'Browse guides, FAQs, and step-by-step articles for every section of 2120.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="2" width="14" height="16" rx="1.5" />
          <path d="M7 7h6M7 10.5h6M7 14h4" />
        </svg>
      ),
      cta: 'Open Help Center',
      // Phase 84.15 \u2014 was `window.open('https://2120.money/help')`. Now
      // routes to the in-product Help Center. Uses window.location so
      // the anchor works even if the settings page is rendered in a
      // context that lacks a React Router navigate helper.
      action: () => { window.location.href = '/help'; },
      testId: 'settings-help-docs',
    },
  ];

  const supportLinks = [
    { label: 'Send feedback',    sub: 'Share what you think — bugs, ideas, or anything on your mind.', href: 'mailto:feedback@2120.money?subject=2120%20feedback' },
    { label: 'Contact support',  sub: 'Reach our support team. Usually responds within one business day.', href: 'mailto:hello@2120.money?subject=2120%20support%20request' },
    { label: 'Status page',      sub: 'Check whether any 2120 services are experiencing issues.', href: 'https://status.2120.money' },
  ];

  return (
    <div className="space-y-5" data-testid="settings-help-section">
      <SectionHeader title="Help & Orientation" subtitle="Get to know 2120, find answers, and get in touch if you need support." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quickActions.map((item) => (
          <SettingsCard key={item.id} className="p-5" testId={`settings-help-card-${item.id}`}>
            <div className="w-10 h-10 rounded-xl bg-offwhite flex items-center justify-center mb-4 text-evergreen">
              {item.icon}
            </div>
            <p className="text-sm font-semibold text-charcoal mb-1.5">{item.label}</p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{item.desc}</p>
            <PrimaryButton
              variant="ghost"
              size="sm"
              onClick={item.action}
              disabled={item.disabled}
              testId={item.testId}
            >
              {item.cta}
            </PrimaryButton>
          </SettingsCard>
        ))}
      </div>

      {/* Phase 84.22 — Demo persona (Priya from Dubai). Rendered outside the
          2-col grid so it always spans the full row — the card contains rich
          messaging (three-step confirm, currency breakdown, load/clear/reset
          controls) that reads much better at full width than squeezed into
          a half-column tile. */}
      <DemoDataSettingsCard />

      <SettingsCard className="px-6 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Get in touch</p>
        <div className="space-y-0">
          {supportLinks.map((row, i) => (
            <div
              key={row.label}
              className="flex items-center gap-4 py-4"
              style={{ borderBottom: i < supportLinks.length - 1 ? '1px solid hsl(var(--border) / 0.5)' : 'none' }}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-charcoal">{row.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{row.sub}</p>
              </div>
              <a
                href={row.href}
                target={row.href.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="text-xs font-medium text-evergreen hover:opacity-70 transition-opacity flex-shrink-0"
                data-testid={`settings-support-link-${row.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {row.label.startsWith('Send') ? 'Open form' : row.label.startsWith('Contact') ? 'Get in touch' : 'View status'} →
              </a>
            </div>
          ))}
        </div>
      </SettingsCard>

      <SettingsCard className="px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-evergreen flex items-center justify-center">
              <span className="font-display text-white text-sm font-semibold">2</span>
            </div>
            <div>
              <p className="text-sm font-medium text-charcoal">2120 Financial Clarity</p>
              <p className="text-xs text-muted-foreground">Version {APP_VERSION}</p>
            </div>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}
